import {
  ADD_AI_ANALYTICS_COMPLETED_ASK,
  ADD_AI_ANALYTICS_MESSAGE,
  ADD_INSTRUCTION,
  ADD_SAVED_CHART,
  CLEAR_AI_ANALYTICS_COMPLETED_ASKS,
  DISMISS_AI_ANALYTICS_ASK_POPUP,
  REMOVE_AI_ANALYTICS_COMPLETED_ASK,
  REMOVE_INSTRUCTION,
  REMOVE_SAVED_CHART,
  RESET_AI_ANALYTICS_ASK_POPUP_DISMISSALS,
  SET_AI_ANALYTICS_ACTIVE_THREAD,
  SET_AI_ANALYTICS_ERROR,
  SET_AI_ANALYTICS_PENDING_ASK,
  SET_AI_ANALYTICS_PENDING_OP,
  SET_AI_ANALYTICS_THREADS,
  SET_AI_ANALYTICS_THREADS_LOADING,
  SET_AI_INSTRUCTIONS_LOADING,
  SET_INSTRUCTIONS,
  SET_SAVED_CHARTS,
  SET_SELECTED_CHART,
  UPDATE_AI_ANALYTICS_MESSAGE,
  UPDATE_AI_ANALYTICS_THREAD,
  UPDATE_INSTRUCTION,
  UPDATE_SAVED_CHART
} from '../actions/aiAnalyticsActionTypes';
import { messageIdOf } from '../components/pages/AiAnalytics/services/aiDataUtils';

const initialState = {
  threads: [],
  activeThread: null,
  threadsLoading: false,
  instructionsLoading: false,
  pendingAsks: {},
  pendingOps: {},
  completedAsks: [],
  dismissedAskPopups: [],
  error: null,
  savedCharts: [],
  selectedChart: null,
  instructions: []
};

export function upsertPendingAsk(state, threadId, ask) {
  const pendingAsks = { ...state.pendingAsks };
  if (ask) {
    pendingAsks[threadId] = { ...pendingAsks[threadId], ...ask };
    return { ...state, pendingAsks };
  }
  delete pendingAsks[threadId];
  const threads = state.threads.map(t => (t.id === threadId && t.activeAsk ? { ...t, activeAsk: null } : t));
  return { ...state, pendingAsks, threads };
}

export function updateMessage(messages, messageId, changes) {
  return (messages || []).map(m => (messageIdOf(m) === messageId ? { ...m, ...changes } : m));
}

export default function aiAnalyticsReducer(state = initialState, action) {
  switch (action.type) {
    case SET_AI_ANALYTICS_THREADS:
      return { ...state, threads: action.payload, error: null };

    case SET_AI_ANALYTICS_ACTIVE_THREAD:
      return {
        ...state,
        activeThread: action.payload ? { ...action.payload, messages: action.payload.messages || [] } : null,
        selectedChart: action.payload ? null : state.selectedChart,
        error: null
      };

    case UPDATE_AI_ANALYTICS_THREAD:
      return {
        ...state,
        threads: state.threads.map(t => (t.id === action.payload.id ? { ...t, ...action.payload } : t)),
        activeThread:
          state.activeThread && state.activeThread.id === action.payload.id
            ? {
                ...state.activeThread,
                ...action.payload,
                messages: action.payload.messages || state.activeThread.messages
              }
            : state.activeThread
      };

    case ADD_AI_ANALYTICS_MESSAGE:
      return {
        ...state,
        activeThread: state.activeThread
          ? {
              ...state.activeThread,
              messages: [...(state.activeThread.messages || []), action.payload]
            }
          : state.activeThread
      };

    case UPDATE_AI_ANALYTICS_MESSAGE:
      return {
        ...state,
        activeThread: state.activeThread
          ? {
              ...state.activeThread,
              messages: updateMessage(state.activeThread.messages, action.payload.messageId, action.payload.changes)
            }
          : state.activeThread
      };

    case SET_AI_ANALYTICS_PENDING_ASK:
      return upsertPendingAsk(state, action.payload.threadId, action.payload.ask);

    case SET_AI_ANALYTICS_PENDING_OP: {
      const { key, op } = action.payload;
      const pendingOps = { ...state.pendingOps };
      if (op) {
        pendingOps[key] = op;
      } else {
        delete pendingOps[key];
      }
      return { ...state, pendingOps };
    }

    case ADD_AI_ANALYTICS_COMPLETED_ASK:
      return {
        ...state,
        completedAsks: [...state.completedAsks.filter(a => a.queryId !== action.payload.queryId), action.payload],
        dismissedAskPopups: state.dismissedAskPopups.filter(id => id !== action.payload.queryId)
      };

    case REMOVE_AI_ANALYTICS_COMPLETED_ASK:
      return { ...state, completedAsks: state.completedAsks.filter(a => a.queryId !== action.payload) };

    case CLEAR_AI_ANALYTICS_COMPLETED_ASKS:
      return state.completedAsks.length ? { ...state, completedAsks: [] } : state;

    case DISMISS_AI_ANALYTICS_ASK_POPUP:
      return { ...state, dismissedAskPopups: [...state.dismissedAskPopups, action.payload] };

    case RESET_AI_ANALYTICS_ASK_POPUP_DISMISSALS:
      return state.dismissedAskPopups.length ? { ...state, dismissedAskPopups: [] } : state;

    case SET_AI_ANALYTICS_THREADS_LOADING:
      return { ...state, threadsLoading: action.payload };

    case SET_AI_INSTRUCTIONS_LOADING:
      return { ...state, instructionsLoading: action.payload };

    case SET_AI_ANALYTICS_ERROR:
      return { ...state, error: action.payload };

    case ADD_SAVED_CHART:
      return { ...state, savedCharts: [action.payload, ...state.savedCharts] };

    case UPDATE_SAVED_CHART:
      return {
        ...state,
        savedCharts: state.savedCharts.map(c => (c.id === action.payload.id ? { ...c, ...action.payload } : c)),
        selectedChart:
          state.selectedChart && state.selectedChart.id === action.payload.id
            ? { ...state.selectedChart, ...action.payload }
            : state.selectedChart
      };

    case REMOVE_SAVED_CHART:
      return {
        ...state,
        savedCharts: state.savedCharts.filter(c => c.id !== action.payload),
        selectedChart: state.selectedChart && state.selectedChart.id === action.payload ? null : state.selectedChart
      };

    case SET_SAVED_CHARTS:
      return { ...state, savedCharts: action.payload };

    case SET_SELECTED_CHART:
      return { ...state, selectedChart: action.payload };

    case SET_INSTRUCTIONS:
      return { ...state, instructions: action.payload, error: null };

    case ADD_INSTRUCTION:
      return {
        ...state,
        instructions: [action.payload, ...state.instructions.filter(i => i.id !== action.payload.id)],
        error: null
      };

    case UPDATE_INSTRUCTION:
      return {
        ...state,
        instructions: state.instructions.map(i => (i.id === action.payload.id ? { ...i, ...action.payload } : i)),
        error: null
      };

    case REMOVE_INSTRUCTION:
      return { ...state, instructions: state.instructions.filter(i => i.id !== action.payload), error: null };

    default:
      return state;
  }
}
